/**
 * Dev-operator theme-token tuning (docs/05-ux-ui-guidelines.md §6).
 *
 * Live overrides for the CONTROL + SEMANTIC color layers, applied as INLINE custom
 * properties on <html>. An inline custom property on the root beats every stylesheet
 * `:root` / `@media` / `[data-theme]` declaration for that property AND re-resolves
 * every var()-derived token (`--control-bright/soft/line`, `--focus`, `--accent`,
 * `--good`, `--choose-soft/line` …) — so setting ONE value recolors the whole app
 * instantly. Overrides are persisted PER-THEME and re-applied on boot and whenever the
 * active theme flips (explicit toggle or OS change).
 *
 * The BRAND layer is deliberately NOT tunable here: the mark is scope-coupled to the
 * platform `personal` accent (web-kit SPACE_ACCENTS) and must never move when the
 * operator tunes control/success. See the three-role token model in index.html :root.
 */

export type ThemeName = "light" | "dark"

/** Which VALUE-bearing token the panel exposes (aliases re-resolve from these). */
export interface TunableToken {
  readonly cssVar: string
  readonly label: string
  readonly note: string
  readonly layer: "control" | "semantic" | "nav"
}

export const TUNABLE_TOKENS: readonly TunableToken[] = [
  { cssVar: "--control", label: "Steuerungsfarbe", note: "Buttons, Links, Fokus, Slider — die interaktive Farbe.", layer: "control" },
  { cssVar: "--nav-accent", label: "Seitennavigation", note: "Aktueller Eintrag in der linken Navigation — eigener Regler, unabhängig von der Steuerungsfarbe.", layer: "nav" },
  { cssVar: "--choose", label: "Erfolg / Passung", note: "Bestätigt, Treffer, Passung (Teal). Nur Erfolgszustände.", layer: "semantic" },
  { cssVar: "--meh", label: "Warnung", note: "»eher nicht«, Achtung (Amber).", layer: "semantic" },
  { cssVar: "--nogo", label: "Kritisch", note: "Veto, Fehler (Rot).", layer: "semantic" },
]

/** Curated CONTROL candidates. Scope-coherent default (operator ruling 2026-07-28):
 *  career = the `personal` space, so control is the PEACH FAMILY. DARK-THEME-FIRST:
 *  each preset's dark value is the primary pick (luminous on charcoal, ≥4.5:1 both as
 *  link-on-ground and ink-on-fill); the light value is its ink-depth derivative for
 *  paper. Warm options first, two calm cool alternates kept for divergence. */
export interface ControlPreset {
  readonly id: string
  readonly label: string
  readonly light: string
  readonly dark: string
}
export const CONTROL_PRESETS: readonly ControlPreset[] = [
  { id: "terracotta", label: "Terrakotta (Standard)", light: "#a4512a", dark: "#dd8a46" },
  { id: "kupfer", label: "Kupfer", light: "#9c4a24", dark: "#e2925e" },
  { id: "rost", label: "Dunkler Rost", light: "#8f4520", dark: "#eda06e" },
  { id: "indigo", label: "Indigo (kühl)", light: "#4a54c4", dark: "#9aa2f2" },
  { id: "slate", label: "Schiefer-Blau (kühl)", light: "#3f6493", dark: "#8fb4e0" },
]

const STORAGE_KEY = "career-tokens"

export interface Overrides {
  light: Record<string, string>
  dark: Record<string, string>
}

const emptyOverrides = (): Overrides => ({ light: {}, dark: {} })

/** Tolerant read of the persisted overrides (localStorage is an untrusted boundary). */
export function loadOverrides(): Overrides {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY)
    if (!raw) return emptyOverrides()
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object") return emptyOverrides()
    const o = parsed as Record<string, unknown>
    const pick = (v: unknown): Record<string, string> => {
      const out: Record<string, string> = {}
      if (v && typeof v === "object") {
        for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
          if (typeof val === "string" && k.startsWith("--")) out[k] = val
        }
      }
      return out
    }
    return { light: pick(o["light"]), dark: pick(o["dark"]) }
  } catch {
    return emptyOverrides()
  }
}

function persist(o: Overrides): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(o))
  } catch {
    /* storage unavailable — the live apply still worked for this session. */
  }
}

/** The theme the app is currently SHOWING (explicit data-theme, else the OS preference). */
export function effectiveTheme(): ThemeName {
  const attr = document.documentElement.getAttribute("data-theme")
  if (attr === "light" || attr === "dark") return attr
  try {
    return globalThis.matchMedia?.("(prefers-color-scheme: dark)")?.matches ? "dark" : "light"
  } catch {
    return "light"
  }
}

/** Clear every tunable inline prop, then set the ACTIVE theme's overrides. */
export function applyOverrides(o: Overrides = loadOverrides()): void {
  if (typeof document === "undefined") return
  const el = document.documentElement
  const set = o[effectiveTheme()]
  for (const t of TUNABLE_TOKENS) {
    const v = set[t.cssVar]
    if (v) el.style.setProperty(t.cssVar, v)
    else el.style.removeProperty(t.cssVar)
  }
}

let installed = false
/** Apply now + re-apply whenever the active theme flips. Idempotent; call once at boot. */
export function installThemeTokens(): void {
  if (installed || typeof document === "undefined") return
  installed = true
  applyOverrides()
  try {
    new MutationObserver(() => applyOverrides()).observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    })
    globalThis
      .matchMedia?.("(prefers-color-scheme: dark)")
      ?.addEventListener?.("change", () => applyOverrides())
  } catch {
    /* observers unavailable — boot-time apply still took effect. */
  }
}

// ─── Panel mutations (each persists + re-applies live) ────────────────────────────

export function getOverride(theme: ThemeName, cssVar: string): string | undefined {
  return loadOverrides()[theme][cssVar]
}

export function setOverride(theme: ThemeName, cssVar: string, value: string): void {
  const o = loadOverrides()
  o[theme][cssVar] = value
  persist(o)
  applyOverrides(o)
}

/** Apply a preset's light + dark pair at once (so both themes stay coherent). */
export function setPresetPair(cssVar: string, light: string, dark: string): void {
  const o = loadOverrides()
  o.light[cssVar] = light
  o.dark[cssVar] = dark
  persist(o)
  applyOverrides(o)
}

export function clearOverride(theme: ThemeName, cssVar: string): void {
  const o = loadOverrides()
  delete o[theme][cssVar]
  persist(o)
  applyOverrides(o)
}

export function clearAll(): void {
  const empty = emptyOverrides()
  persist(empty)
  applyOverrides(empty)
}

/** The currently RESOLVED value of a token (override or theme default) for the active
 *  theme — used to seed the color inputs. Normalised to #rrggbb for <input type=color>. */
export function resolvedHex(cssVar: string): string {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim()
    return toHex6(raw)
  } catch {
    return "#000000"
  }
}

/** Expand #abc → #aabbcc and lowercase; anything unparseable falls back to #000000. */
export function toHex6(value: string): string {
  const v = value.trim().toLowerCase()
  if (/^#[0-9a-f]{6}$/.test(v)) return v
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`
  return "#000000"
}

/** Ready-to-paste CSS snippet pinning the operator's chosen literals (the theme-config
 *  analogue of career-cv-print's "pin literal values" export). Empty when nothing set. */
export function exportCss(): string {
  const o = loadOverrides()
  const block = (selector: string, set: Record<string, string>): string => {
    const lines = Object.entries(set).map(([k, v]) => `  ${k}: ${v};`)
    return lines.length ? `${selector} {\n${lines.join("\n")}\n}` : ""
  }
  return [
    block(':root, :root[data-theme="light"]', o.light),
    block(':root[data-theme="dark"]', o.dark),
  ]
    .filter(Boolean)
    .join("\n\n")
}
